class LussoFoundation {
  constructor() {
    this.observer = null;
    this.observedElements = new Set();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handleIntersect = this.handleIntersect.bind(this);
    this.handleMotionPreference = this.handleMotionPreference.bind(this);
  }

  get motionEnabled() {
    return document.body?.dataset.lussoMotion !== 'off' && !this.reducedMotion.matches;
  }

  start() {
    this.reducedMotion.addEventListener?.('change', this.handleMotionPreference);
    this.configureObserver();
    this.init(document);

    document.addEventListener('shopify:section:load', (event) => this.init(event.target));
    document.addEventListener('shopify:section:unload', (event) => this.release(event.target));
  }

  configureObserver() {
    this.observer?.disconnect();
    this.observer = null;

    if (!this.motionEnabled || !('IntersectionObserver' in window)) {
      document.documentElement.classList.remove('ls-motion-ready');
      this.revealAll();
      return;
    }

    this.observer = new IntersectionObserver(this.handleIntersect, {
      root: null,
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.18,
    });

    document.documentElement.classList.add('ls-motion-ready');
  }

  init(root) {
    const elements = root.matches?.('[data-lusso-reveal]')
      ? [root, ...root.querySelectorAll('[data-lusso-reveal]')]
      : [...root.querySelectorAll('[data-lusso-reveal]')];

    elements.forEach((element) => {
      if (element.dataset.lussoRevealReady === 'true') return;

      element.dataset.lussoRevealReady = 'true';

      if (!this.observer) {
        element.classList.add('is-visible');
        return;
      }

      this.observedElements.add(element);
      this.observer.observe(element);
    });
  }

  release(root) {
    this.observedElements.forEach((element) => {
      if (element === root || root.contains(element)) {
        this.observer?.unobserve(element);
        this.observedElements.delete(element);
      }
    });
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      entry.target.classList.add('is-visible');
      this.observer?.unobserve(entry.target);
      this.observedElements.delete(entry.target);
    });
  }

  handleMotionPreference() {
    this.observedElements.forEach((element) => {
      element.classList.remove('is-visible');
      delete element.dataset.lussoRevealReady;
    });
    this.observedElements.clear();
    this.configureObserver();
    this.init(document);
  }

  revealAll() {
    this.observedElements.forEach((element) => element.classList.add('is-visible'));
    this.observedElements.clear();
    document.querySelectorAll('[data-lusso-reveal]').forEach((element) => element.classList.add('is-visible'));
  }
}

const lussoFoundation = new LussoFoundation();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => lussoFoundation.start(), { once: true });
} else {
  lussoFoundation.start();
}
